export interface Section {
	/** will be rendered in <h3> */
	title?: string;
	/** will be rendered in <p> */
	text?: string | string[];
	/** will be rendered in <ul> */
	points?: string[];
	/** subsections with titles rendered in <h4> */
	subsections?: {
		title?: string;
		text?: string | string[];
		points?: string[];
	}[];
}

export interface Job {
	name: string;
	type: string;
	title: string;
	description: string;
	route: string;
	sections: Section[];
}

export const jobs: Job[] = [
	{
		name: 'Senior Full-Stack Engineer',
		type: 'Full-time',
		title: 'Senior Full-Stack Engineer | SitePen',
		description:
			'At SitePen, senior engineers are trusted to do meaningful work with other highly capable people. We are looking for a Senior Full-Stack Engineer who values autonomy, strong peers, interesting work, and a company that respects life outside work.',
		route: 'senior-full-stack-engineer',
		sections: [
			{
				text: [
					'At SitePen, senior engineers are trusted to do meaningful work with other highly capable people. We are a long-standing remote software consultancy known for solving difficult problems, building quality systems, and helping customers move faster with confidence.',
					'If you want endless layers of management, rigid process, small, detailed tasks, or to be boxed into one narrow specialty, this is not that role.',
					'If you want autonomy, strong peers, interesting work, and a company that respects your life outside work, keep reading.'
				]
			},
			{
				title: 'Why SitePen',
				subsections: [
					{
						title: 'Work with Experienced People',
						text:
							'You’ll collaborate with seasoned engineers, designers, and technical leaders who know how to build and deliver quality software. We value trust, professionalism, and giving talented people the space to do their best work.'
					},
					{
						title: 'Remote Done Right',
						text:
							'We’ve successfully operated as a remote company for many years. No sudden return-to-office changes, no unnecessary office politics. Work from anywhere in the United States.'
					},
					{
						title: 'Full-Stack Means Opportunity',
						text:
							'You won’t be limited to one narrow lane. Our engineers contribute where they create the most impact: frontend, backend, architecture, APIs, performance, delivery, and customer collaboration.'
					},
					{
						title: 'AI-Forward Engineering Culture',
						text:
							'We believe modern engineering combines deep experience with thoughtful use of AI tools. You’ll be encouraged to use today’s best AI-assisted workflows to move faster, improve quality, and reduce repetitive work.'
					},
					{
						title: 'Real Work-Life Balance',
						text:
							'We believe sustainable excellence wins over burnout. Strong people doing meaningful work over the long term creates the best outcomes for everyone.'
					},
					{
						title: 'Meaningful Projects',
						text:
							'You’ll work on customer products, modernization efforts, new systems, and interesting technical challenges that make a real difference.'
					}
				]
			},
			{
				title: 'What This Role Actually Is',
				text: [
					'This is a senior individual contributor role for someone who leads through capability, sound judgment, and steady execution.',
					'You will:'
				],
				points: [
					'Build and deliver production software across the stack',
					'Architect practical solutions grounded in business needs',
					'Work directly with customers, product leaders, and engineering teams',
					'Improve systems that are slow, fragile, outdated, or difficult to scale',
					'Raise standards through example and collaboration',
					'Make strong technical decisions and clearly explain tradeoffs',
					'Support other engineers through reviews, mentoring, and leadership'
				]
			},
			{
				text:
					'You’ll be trusted to work independently and help bring clarity to complex situations.'
			},
			{
				title: 'What We’re Looking For',
				subsections: [
					{
						title: 'Strong Senior-Level Experience',
						text:
							'Typically 15+ years of full-time software engineering experience, though exceptional candidates with fewer years are welcome.'
					},
					{
						title: 'Full-Stack Depth',
						text:
							'Comfortable owning problems from browser to database to cloud infrastructure.'
					},
					{
						title: 'Customer-Facing Communication',
						text:
							'Able to communicate effectively with engineers, product teams, and executives.'
					},
					{
						title: 'Ownership Mentality',
						text:
							'Cares about outcomes, reliability, maintainability, and leaving systems better than they were found.'
					},
					{
						title: 'Good Judgment',
						text:
							'Knows when to move quickly, when to be deliberate, and when to challenge assumptions constructively.'
					}
				]
			},
			{
				title: 'AI-First Expectations',
				text:
					'This role assumes strong experience with AI-assisted software development and the ability to use modern tooling effectively across day-to-day engineering work, including:',
				points: [
					'Solution design and technical exploration',
					'Coding and implementation speed',
					'Refactoring legacy systems',
					'Debugging and root-cause analysis',
					'Test generation and automation',
					'Documentation and knowledge transfer',
					'Research and rapid prototyping'
				]
			},
			{
				text:
					'We value engineers who know how to validate outputs, maintain quality, protect security standards, and use sound judgment while leveraging AI to improve execution.'
			},
			{
				title: 'Technical Areas You May Work In',
				text:
					'We care more about depth and adaptability than keyword matching, but experience in many of these areas is valuable.',
				subsections: [
					{
						title: 'Frontend',
						points: [
							'TypeScript / JavaScript',
							'Angular, React, or modern web frameworks',
							'HTML / CSS',
							'Performance optimization',
							'Accessibility',
							'Large-scale UI architecture'
						]
					},
					{
						title: 'Backend',
						points: [
							'Java / Spring Boot',
							'TypeScript',
							'Python',
							'API design (REST, GraphQL, RPC)',
							'Authentication / authorization',
							'Caching, resiliency, scalability'
						]
					},
					{
						title: 'Data & Infrastructure',
						points: [
							'SQL and schema design',
							'Performance tuning',
							'AWS or comparable cloud platforms',
							'CI/CD pipelines',
							'Containers and distributed systems',
							'Integrating real-world data sources'
						]
					}
				]
			},
			{
				title: 'What Success Looks Like',
				text: 'In this role, you will:',
				points: [
					'Solve challenging technical problems efficiently',
					'Build trust with customers and teammates',
					'Improve delivery speed while maintaining quality',
					'Use AI to increase effectiveness',
					'Help other engineers grow stronger',
					'Bring calm, practical leadership to ambiguous situations'
				]
			},
			{
				title: 'What You’ll Appreciate Here',
				points: [
					'Trust and autonomy',
					'Talented peers',
					'Practical processes',
					'Respect for life outside work',
					'Interesting technical challenges',
					'Long-term thinking over short-term chaos'
				]
			},
			{
				title: 'Compensation & Benefits',
				text:
					'We offer competitive compensation based on experience and impact, along with benefits that support a sustainable career and life outside work.'
			},
			{
				title: 'Job Location',
				text: 'Fully remote within the United States.'
			},
			{
				title: 'To Apply',
				text: [
					'Send your resume, a short introduction, and anything that helps us understand how you think and build. Relevant work samples, GitHub, architecture examples, or case studies are welcome.',
					'We care less about buzzwords and more about capability.',
					'If you’re a serious engineer who has embraced the future of software development, we’d love to hear from you!'
				]
			}
		]
	},
	{
		name: 'Senior Technical Project Manager',
		type: '6-Month Contract',
		title: 'Senior Technical Project Manager | SitePen',
		description:
			'We are looking for a seasoned Senior Technical Project Manager to help lead and organize the delivery of a high-quality mobile application initiative. This is a hands-on leadership role for someone who brings structure without bureaucracy and keeps teams aligned, accountable, and shipping.',
		route: 'senior-technical-project-manager',
		sections: [
			{
				text: [
					'We are looking for a seasoned Senior Technical Project Manager to help lead and organize the delivery of a high-quality mobile application initiative. This is a hands-on leadership role for someone who understands how modern software products are built, knows how to keep teams aligned and accountable, and can confidently manage both internal execution and external client relationships.',
					'The right person brings structure without bureaucracy, keeps momentum high, and understands that successful delivery comes from balancing product priorities, engineering realities, timelines, communication, and quality.'
				]
			},
			{
				title: 'We are looking for someone who is',
				points: [
					'A senior project manager with significant experience leading mobile application projects from planning through release',
					'Highly experienced working with product stakeholders, business leaders, clients, designers, and engineering teams',
					'Comfortable owning delivery for a modern mobile application and the supporting web/platform ecosystem behind it',
					'Technically fluent across the full software lifecycle including product discovery, design, development, QA, release management, analytics, and support',
					'Able to anticipate what teams may overlook and proactively ensure important requirements, dependencies, approvals, and deliverables are not missed',
					'Strong in roadmap ownership, milestone planning, prioritization, scheduling, and timeline management',
					'Deep Jira expertise with the ability to build effective workflows, boards, dashboards, and linked ticket structures that create clear visibility, accountability, and execution discipline rather than a backlog graveyard',
					'Experienced managing client relationships with professionalism, confidence, and clear communication',
					'Able to translate business goals into practical execution plans for designers and developers',
					'Focused on outcomes, accountability, and consistent forward progress rather than administrative busywork',
					'Skilled at identifying delivery risks early and driving resolution before they become blockers',
					'Experienced planning and driving hands-on mobile user testing across devices, operating systems, and real-world usage scenarios to uncover issues early and improve overall product quality',
					'Comfortable holding teams to commitments while maintaining strong working relationships',
					'Organized, detail-oriented, and calm under pressure',
					'Self-directed and effective in a fully remote environment'
				]
			},
			{
				title: 'What this role is not',
				points: [
					'A passive ticket writer',
					'A meeting scheduler without ownership',
					'A status reporter waiting on others to lead',
					'A process-heavy administrator creating unnecessary overhead'
				]
			},
			{
				title: 'What this role is',
				points: [
					'The steady hand helping a mobile product ship successfully',
					'The person ensuring priorities are clear, time is used well, and commitments are met',
					'A trusted partner to clients, product leaders, and engineering teams',
					'An operator who uses Jira intelligently to create clarity, accountability, and momentum',
					'A driver of quality, predictability, and execution excellence'
				]
			},
			{
				text:
					'If you know how to bring order to complex projects, keep teams focused, and help great products reach the finish line, we would like to hear from you.'
			},
			{
				title: 'To Apply',
				text:
					'To be considered for this role, please send us an email that includes your resume, cover letter, and relevant work samples. Thank you!'
			}
		]
	}
];
